"use client";

import { useRef } from "react";
import { GradText, SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import QrSvg from "@/components/qr-codes/QrSvg";
import { QR } from "@/components/qr-codes/qr-matrix";
import { routePath } from "@/lib/routes";
import LinkRow, { LinkSheet, RowChip } from "./LinkRow";

const QR_SUMMARY =
  "Example of a link's QR code: the code points at the short link go.example.com/summer, so a " +
  "destination edit reaches the printed code too. A fixed parameter, utm_source=flyer, is added in " +
  "the QR editor, and scans show up as QR traffic in analytics.";

const UTM_SUMMARY =
  "Example of campaign tags: go.example.com/summer redirects to example.com/summer-2026 with " +
  "utm_campaign=summer set on the destination with the built-in UTM builder, and the link is shared " +
  "per channel with its own ?utm_source — newsletter, instagram, flyer. Each click records both: the " +
  "channel's utm_source from the short link and the campaign tag on the destination it was sent to, " +
  "shown side by side in analytics.";

/**
 * How links look when shared — the last basic: what a link carries BEFORE
 * the click. Left: the link's QR code (it points at the short link, so edits
 * reach print; fixed parameters come from the QR editor's "Additional
 * Parameters"). Right: campaign tags as they work since backend#478 — the
 * campaign set on the destination once with the dashboard's UTM builder,
 * ?utm_source added per channel on the shared URL, and both recorded on each
 * click (reporting is owned by /analytics). There is still no preview (OG)
 * override (claims audit 2026-09-27), so none is drawn.
 */
export default function ShareSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-rise]", { y: 22, stagger: 0.08 });

  return (
    <SectionShell id="share" className="scroll-mt-16">
      <SectionHead
        eyebrow="How links look when shared"
        title={
          <>
            One link, <GradText>tagged per channel.</GradText>
          </>
        }
        lead="Print it as a QR code that follows every edit, tag the destination with the built-in UTM builder, and share it with its own utm_source per channel — each click records both, no extra tool."
      />

      <div ref={rootRef} className="flex flex-col">
        <div data-grid-block className="grid items-start gap-[46px] lg:grid-cols-2">
          {/* QR code on the same link */}
          <div data-rise className="min-w-0">
            <p className="sr-only">{QR_SUMMARY}</p>
            <div aria-hidden="true" className="rounded-ref-lg border border-line bg-surface p-5 shadow-ref-lg sm:p-6">
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex-none rounded-[10px] border border-line p-3">
                  <QrSvg path={QR.sq} width={112} className="block" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-jetbrains text-[10.5px] tracking-[0.12em] text-dim">QR CODE · SAME LINK</div>
                  <div className="mt-1.5 font-jetbrains text-[13px] font-semibold text-ink">go.example.com/summer</div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <RowChip accent>utm_source · flyer</RowChip>
                    <RowChip>scans counted as QR</RowChip>
                  </div>
                  <div className="mt-3 font-jetbrains text-[11px] text-dim">destination edit ⟶ the print follows</div>
                </div>
              </div>
            </div>
            <h3 className="mt-5 text-[17px] font-bold tracking-[-0.01em] text-ink">A QR code on the same link</h3>
            <p className="mt-2 max-w-[46ch] text-[14.5px] leading-[1.6] text-muted">
              The code points at the short link, so a destination edit reaches the printed code too.
              Fixed parameters — utm_source=flyer, say — are set in the QR editor, and scans show
              up as QR traffic in analytics. More on{" "}
              <a href={routePath("qrCodes")} className="font-semibold text-ink underline decoration-line-3 underline-offset-[3px] hover:text-brand-600">
                QR codes
              </a>
              .
            </p>
          </div>

          {/* Campaign tags per channel */}
          <div data-rise className="min-w-0">
            <p className="sr-only">{UTM_SUMMARY}</p>
            <div aria-hidden="true">
              <LinkSheet label="ONE LINK" note="CAMPAIGN TAGS">
                <LinkRow
                  slug="go.example.com/summer"
                  destination={
                    <>
                      example.com/summer-2026
                      <span className="text-brand-600">?utm_campaign=summer</span>
                    </>
                  }
                  meta="set once"
                  wrapDestination
                  extras={
                    <>
                      <RowChip accent>?utm_source=newsletter</RowChip>
                      <RowChip accent>?utm_source=instagram</RowChip>
                      <RowChip accent>?utm_source=flyer</RowChip>
                    </>
                  }
                />
              </LinkSheet>
            </div>
            <h3 className="mt-5 text-[17px] font-bold tracking-[-0.01em] text-ink">Campaign tags, per channel</h3>
            <p className="mt-2 max-w-[46ch] text-[14.5px] leading-[1.6] text-muted">
              Set the campaign’s UTMs on the destination once with the UTM builder — source and medium
              lowercased, no duplicate tags — then share the link with its own ?utm_source per
              channel. Each click records the tags on the short link it came through and on the
              destination it was sent to, and both break down side by side in{" "}
              <a href={routePath("analytics")} className="font-semibold text-ink underline decoration-line-3 underline-offset-[3px] hover:text-brand-600">
                analytics
              </a>
              ; the campaign playbook is on{" "}
              <a href={routePath("solutionsMarketing")} className="font-semibold text-ink underline decoration-line-3 underline-offset-[3px] hover:text-brand-600">
                the marketing page
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
